import React from 'react';

import { FlexProps } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfigurationSection } from 'components/SmartAssistantSections/ConfigurationSection';
import { useCommonConfigurationForm } from 'hooks/SmartAssistantForms/use-common-configuration-form';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant, SmartAssistantType } from 'shared/interfaces/SmartAssistant';

interface ConfigurationManagementSectionProps extends FlexProps {
  smartAssistant: SmartAssistant;
  forceAlignVertical?: boolean;
  onSaveClbck?: () => void;
}

export function ConfigurationManagementSection({
  smartAssistant,
  forceAlignVertical,
  onSaveClbck,
  ...flexProps
}: ConfigurationManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const configurationForm = useCommonConfigurationForm(smartAssistant.type, smartAssistant);

  const saveMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      configurationForm.resetDirty();
      onSaveClbck?.();
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_CONFIGURATION');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_CONFIGURATION')
  });

  const onSave = () => {
    const validation = configurationForm.validate();
    if (validation.hasErrors) return;
    saveMutation.mutate({
      id: smartAssistant._id,
      ...configurationForm.values,
      customSystemPrompt: configurationForm.values.customSystemPrompt,
      model: configurationForm.values.model !== smartAssistant.model ? configurationForm.values.model : undefined
    });
  };

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.CONFIGURATION_SECTION.TITLE')}
      hasChanges={configurationForm.isDirty()}
      loading={saveMutation.isLoading}
      onSave={onSave}
      onCancel={() => configurationForm.reset()}>
      <ConfigurationSection
        form={configurationForm}
        forceAlignVertical={forceAlignVertical}
        smartAssistantType={smartAssistant.type}
        voiceModelsOnly={smartAssistant.type === SmartAssistantType.PHONE}
        {...flexProps}
      />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
